/**
 * RequireAuth - Protege rotas que requerem autenticação
 * Redireciona para /login com parâmetro next para voltar após login
 */
import { Navigate, useLocation } from 'react-router-dom';
import { storage } from '@/lib/storage';
import React, { useEffect, useState, useCallback, useRef } from 'react';

export function RequireAuth({ children }) {
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  const checkAuth = useCallback(() => {
    const token = storage.getToken();
    const user = storage.getUser();
    const authenticated = !!(token && user);
    
    console.log('[RequireAuth] Checking auth:', { 
      hasToken: !!token, 
      hasUser: !!user, 
      authenticated,
      path: location.pathname 
    });
    
    setIsAuthenticated(authenticated);
    setIsChecking(false);
  }, [location.pathname]);
  
  useEffect(() => {
    // Check authentication on mount
    checkAuth();
    
    // Listen for auth changes (e.g., when 401 interceptor clears auth)
    const handleAuthChange = (event) => {
      console.log('[RequireAuth] Auth changed event received:', event?.detail);
      checkAuth();
    };
    
    // Listen for storage changes (cross-tab sync)
    const handleStorageChange = (event) => {
      if (event.key === 'magicstay_token' || event.key === 'magicstay_user' || event.key === null) {
        console.log('[RequireAuth] Storage changed:', event.key);
        checkAuth();
      }
    };
    
    window.addEventListener('authChanged', handleAuthChange);
    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically in case events are missed
    const intervalId = setInterval(checkAuth, 5000);
    
    return () => {
      window.removeEventListener('authChanged', handleAuthChange);
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };
  }, [checkAuth]);
  
  // Show nothing while checking (prevents flash of content)
  if (isChecking) {
    return null;
  }
  
  // Check if user is authenticated
  if (!isAuthenticated) {
    console.log('[RequireAuth] Not authenticated, redirecting to login');
    // Redirect to login with the current path as return URL
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${returnUrl}`} replace />;
  }
  
  return children;
}

/**
 * RequireRole - Protege rotas que requerem papel específico
 */
export function RequireRole({ children, allowedRoles = [] }) {
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [authState, setAuthState] = useState({ isAuthenticated: false, hasRole: false });
  const checkCountRef = useRef(0);
  
  useEffect(() => {
    const checkAuth = () => {
      const token = storage.getToken();
      const user = storage.getUser();
      const isAuthenticated = !!(token && user);
      const userRole = user?.role || 'guest';
      const hasRole = allowedRoles.length === 0 || allowedRoles.includes(userRole);
      
      console.log('[RequireRole] Checking:', { 
        isAuthenticated, 
        userRole, 
        allowedRoles, 
        hasRole,
        path: location.pathname,
        checkCount: checkCountRef.current
      });
      
      // If not authenticated or no role yet, retry a few times with small delays
      // This handles race conditions where localStorage isn't updated yet after login
      if ((!isAuthenticated || !hasRole) && checkCountRef.current < 3) {
        checkCountRef.current++;
        setTimeout(() => checkAuth(), 100);
        return;
      }
      
      setAuthState({ isAuthenticated, hasRole });
      setIsChecking(false);
    };
    
    // Reset check count on mount
    checkCountRef.current = 0;
    checkAuth();
    
    const handleAuthChange = () => {
      checkCountRef.current = 0;
      checkAuth();
    };
    
    window.addEventListener('authChanged', handleAuthChange);
    window.addEventListener('storage', handleAuthChange);
    
    return () => {
      window.removeEventListener('authChanged', handleAuthChange);
      window.removeEventListener('storage', handleAuthChange);
    };
  }, [allowedRoles, location.pathname]);
  
  if (isChecking) {
    return null;
  }
  
  // First check authentication
  if (!authState.isAuthenticated) {
    console.log('[RequireRole] Not authenticated, redirecting to login');
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${returnUrl}`} replace />;
  }
  
  // Then check role
  if (!authState.hasRole) {
    console.log('[RequireRole] No permission, redirecting to unauthorized');
    // User is authenticated but doesn't have permission
    return <Navigate to="/unauthorized" replace />;
  }
  
  return children;
}

export default RequireAuth;
